import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AuditEvent } from '../gen/types'

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

const SKELETON_WIDTHS = ['120px', '160px', '140px', '160px', '60px']

function SkeletonRows({ count = 8 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <tr key={i} className="border-b border-border-subtle">
          {SKELETON_WIDTHS.map((w, j) => (
            <td key={j} className="py-2 px-3">
              <div
                className="h-3 rounded-[2px] bg-overlay/40"
                style={{
                  width: w,
                  animation: 'typingBounce 1.5s ease-in-out infinite',
                  animationDelay: `${(i + j) * 0.1}s`,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function DetailsCell({ payload }: { payload: Record<string, unknown> | undefined }) {
  const [open, setOpen] = useState(false)
  const hasPayload = payload != null && Object.keys(payload).length > 0
  if (!hasPayload) {
    return <span className="text-fg-dim">—</span>
  }
  return (
    <div>
      <button className="btn btn-default" onClick={() => setOpen((v) => !v)} type="button">
        {open ? 'Hide' : 'Show'}
      </button>
      {open && (
        <pre className="mt-1.5 m-0 max-w-[420px] overflow-auto bg-canvas border border-border-subtle rounded-[3px] p-2 font-mono text-[10px] text-fg-muted whitespace-pre-wrap">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  )
}

function KindIdCell({ kind, id }: { kind: string; id: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.04em] text-fg-dim border border-border-subtle rounded-[3px] px-1.5 py-[1px] font-mono">
        {kind}
      </span>
      <span className="text-[11px] text-fg-muted font-mono truncate">{id}</span>
    </div>
  )
}

function AuditRow({ event }: { event: AuditEvent }) {
  return (
    <tr className="border-b border-border-subtle hover:bg-raised">
      <td className="py-2 px-3 text-[11px] text-fg-dim font-mono whitespace-nowrap">
        {formatDateTime(event.at)}
      </td>
      <td className="py-2 px-3">
        <KindIdCell kind={event.actorKind} id={event.actorId} />
      </td>
      <td className="py-2 px-3 text-[12px] text-fg font-mono">{event.action}</td>
      <td className="py-2 px-3">
        <KindIdCell kind={event.targetKind} id={event.targetId} />
      </td>
      <td className="py-2 px-3">
        <DetailsCell payload={event.payload} />
      </td>
    </tr>
  )
}

export default function AdminPage() {
  const [pageToken, setPageToken] = useState<string | undefined>(undefined)
  const [allEvents, setAllEvents] = useState<AuditEvent[]>([])
  const [nextToken, setNextToken] = useState<string | undefined>(undefined)

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['auditLog', pageToken] as const,
    queryFn: () => api.listAuditLog({ pageSize: 50, pageToken }),
    staleTime: 30_000,
    gcTime: 60_000,
  })

  useEffect(() => {
    if (!data) return
    const incoming = data.events ?? []
    if (pageToken === undefined) {
      setAllEvents(incoming)
    } else {
      setAllEvents((prev) => [...prev, ...incoming])
    }
    setNextToken(data.nextPageToken || undefined)
  }, [data, pageToken])

  const handleLoadMore = () => {
    if (nextToken) setPageToken(nextToken)
  }

  const pageHeader = (
    <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
      <div>
        <h1 className="text-[22px] font-bold tracking-[-0.005em] text-fg m-0">Audit Log</h1>
        <p className="m-0 mt-1 text-[12px] text-fg-dim">
          All administrative events across the tenant
        </p>
      </div>
      {allEvents.length > 0 && (
        <span className="font-mono text-[11px] text-fg-muted border border-border-default bg-surface rounded-[3px] px-2 py-1">
          {allEvents.length} event{allEvents.length !== 1 ? 's' : ''}
          {nextToken ? '+' : ''}
        </span>
      )}
    </div>
  )

  const tableShell = (body: React.ReactNode) => (
    <div className="card overflow-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="[&>th]:py-2 [&>th]:px-3 [&>th]:text-left [&>th]:bg-raised [&>th]:text-fg-dim [&>th]:font-semibold [&>th]:text-[10px] [&>th]:uppercase [&>th]:tracking-[0.06em] [&>th]:border-b [&>th]:border-border-default">
            <th>Date / Time</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Target</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  )

  const container = (children: React.ReactNode) => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-[1100px] mx-auto px-7 py-8">
        {pageHeader}
        {children}
      </div>
    </div>
  )

  if (isLoading) {
    return container(tableShell(<SkeletonRows count={10} />))
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : 'Failed to load audit log'
    return container(
      <div className="flex items-center gap-2 text-sev-blocking text-[13px] p-5">
        <span aria-hidden>⚠</span>
        <span>{msg}</span>
      </div>,
    )
  }

  if (allEvents.length === 0) {
    return container(
      <div className="flex items-center gap-2 text-fg-dim text-[13px] p-5">
        <span aria-hidden>—</span>
        <span>No audit events yet.</span>
      </div>,
    )
  }

  return container(
    <>
      {tableShell(
        <>
          {allEvents.map((event) => (
            <AuditRow key={event.id} event={event} />
          ))}
          {isFetching && <SkeletonRows count={3} />}
        </>,
      )}
      {nextToken && (
        <div className="flex justify-center mt-4">
          <button
            className="btn btn-default"
            onClick={handleLoadMore}
            disabled={isFetching}
            type="button"
          >
            {isFetching ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </>,
  )
}
